import { useMemo } from 'react';
import { $, fH, DeptBadge } from './utils';

/**
 * PTO & Absence tab.
 *
 * Source: public.pto_monthly — a security_invoker view over daily_pto_costed,
 * gated by allowlisted_read_daily_pto on is_allowed_for_app('teamhours').
 *
 * Numbers:
 *  - Days are WORKING days. Weekends are excluded at ingest, and PTO days that
 *    fall on a public holiday for that person are dropped so they aren't
 *    counted twice.
 *  - Holidays (COR leave_type 11) are capacity reductions, not PTO. Shown
 *    separately, never folded into pto_days or pto_cost.
 *  - Cost is DERIVED: hours x that person's monthly rate, recovered from COR's
 *    own per-entry cost arithmetic. It will not tie to monthly_summary.total_cost,
 *    which uses the salaried fixed-cost model.
 *  - Starts June 2026. Earlier PTO exists in daily_pto but predates daily_hours
 *    coverage, so no rate is recoverable and it's excluded from the view.
 *
 * Department note: pto_monthly carries COR's raw category_name ("Creative Team"),
 * while team_analytics carries the worker's mapped taxonomy ("Creative"). We
 * resolve department from `users` by person name so badges and rollups line up
 * with the other tabs, falling back to the raw value when there's no match.
 */
export default function PtoTab({ pto, users, excludeOps }) {
  const rows = useMemo(() => {
    const deptByName = {};
    for (const u of users) deptByName[u.user_name] = u.department;

    return [...pto]
      .map(r => ({
        ...r,
        pto_days:      Number(r.pto_days      || 0),
        pto_hours:     Number(r.pto_hours     || 0),
        pto_cost:      Number(r.pto_cost      || 0),
        holiday_days:  Number(r.holiday_days  || 0),
        holiday_hours: Number(r.holiday_hours || 0),
        dept:          deptByName[r.person] || r.department || 'Unassigned',
        deptMapped:    !!deptByName[r.person],
      }))
      .sort((a, b) => (b.pto_days - a.pto_days) || (b.pto_cost - a.pto_cost));
  }, [pto, users]);

  const byDept = useMemo(() => {
    const m = {};
    for (const r of rows) {
      if (!m[r.dept]) m[r.dept] = { name: r.dept, days: 0, cost: 0, people: 0 };
      m[r.dept].days += r.pto_days;
      m[r.dept].cost += r.pto_cost;
      if (r.pto_days > 0) m[r.dept].people++;
    }
    return Object.values(m).filter(d => d.days > 0).sort((a, b) => b.days - a.days);
  }, [rows]);

  if (rows.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
        No PTO or holiday records for this month.
        <div style={{ fontSize: 12, marginTop: 8 }}>PTO tracking begins June 2026.</div>
      </div>
    );
  }

  const tot = rows.reduce((a, r) => ({
    days:    a.days    + r.pto_days,
    hours:   a.hours   + r.pto_hours,
    cost:    a.cost    + r.pto_cost,
    holDays: a.holDays + r.holiday_days,
  }), { days: 0, hours: 0, cost: 0, holDays: 0 });

  const peopleOff   = rows.filter(r => r.pto_days > 0).length;
  const anyCarried  = rows.some(r => r.any_rate_carried);
  const unmatched   = rows.filter(r => !r.deptMapped);
  const maxDeptDays = Math.max(...byDept.map(d => d.days), 1);
  const maxDeptCost = Math.max(...byDept.map(d => d.cost), 1);

  const kpis = [
    { label: 'PTO Days',     value: tot.days,      sub: 'working days',              color: 'var(--blue)' },
    { label: 'PTO Hours',    value: fH(tot.hours), sub: 'contracted hours',          color: 'var(--text)' },
    { label: 'PTO Cost',     value: $(tot.cost),   sub: 'derived, hours x rate',     color: 'var(--amber)' },
    { label: 'People Off',   value: peopleOff,     sub: `of ${rows.length} tracked`, color: 'var(--purple)' },
    { label: 'Holiday Days', value: tot.holDays,   sub: 'capacity, not PTO',         color: 'var(--text2)' },
  ];

  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12, marginBottom:20 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, padding:16, textAlign:'center' }}>
            <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'.7px', color:'var(--text2)', marginBottom:4 }}>{k.label}</div>
            <div style={{ fontFamily:'DM Mono,monospace', fontSize:22, fontWeight:700, color:k.color, margin:'6px 0 2px' }}>{k.value}</div>
            <div style={{ fontSize:11, color:'var(--text3)' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {anyCarried && (
        <div style={noticeStyle}>
          <span>&#8505;</span>
          <span>
            Rows marked <strong>~</strong> use a <strong>carried-forward rate</strong> — too little
            logged time that month to derive one, so the most recent reliable rate was used.
            These self-correct once hours are logged.
          </span>
        </div>
      )}

      {unmatched.length > 0 && (
        <div style={warnStyle}>
          <span>⚠️</span>
          <span>
            <strong>{unmatched.length}</strong> {unmatched.length === 1 ? 'person has' : 'people have'} PTO
            but no team-analytics row this month ({unmatched.map(u => u.person).join(', ')}).
            Department shown is COR&apos;s raw value.
          </span>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
        <BarCard title="PTO Days by Department">
          {byDept.map(d => (
            <BarRow key={d.name} label={`${d.name} (${d.people})`} value={`${d.days} days`}
              width={d.days / maxDeptDays * 100} color="var(--blue)" />
          ))}
        </BarCard>
        <BarCard title="PTO Cost by Department">
          {[...byDept].sort((a, b) => b.cost - a.cost).map(d => (
            <BarRow key={d.name} label={d.name} value={$(d.cost)}
              width={d.cost / maxDeptCost * 100} color="var(--amber)" />
          ))}
        </BarCard>
      </div>

      <div style={{ overflowX:'auto', borderRadius:12, border:'1px solid var(--border)', background:'var(--card)' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'var(--card2)' }}>
              {['#','Name','Department','PTO Days','PTO Hours','PTO Cost','Holidays'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.user_id} style={{ borderTop:'1px solid var(--border)' }}>
                <td style={monoTd}>{i + 1}</td>
                <td style={{ padding:'9px 14px', fontWeight:600 }}>
                  {r.person}
                  {r.any_rate_carried && (
                    <span title="Rate carried forward from a previous month" style={carriedTag}>~</span>
                  )}
                </td>
                <td style={{ padding:'9px 14px' }}><DeptBadge dept={r.dept} /></td>
                <td style={monoTd}>{r.pto_days || '—'}</td>
                <td style={monoTd}>{r.pto_hours ? fH(r.pto_hours) : '—'}</td>
                <td style={{ ...monoTd, fontWeight:600 }}>{r.pto_cost ? $(r.pto_cost) : '—'}</td>
                <td style={{ ...monoTd, color:'var(--text3)' }}>{r.holiday_days || '—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop:'2px solid var(--green)', background:'var(--card2)', fontWeight:700 }}>
              <td style={monoTd}></td>
              <td style={{ padding:'9px 14px' }}>TOTAL ({rows.length})</td>
              <td style={{ padding:'9px 14px' }}></td>
              <td style={monoTd}>{tot.days}</td>
              <td style={monoTd}>{fH(tot.hours)}</td>
              <td style={monoTd}>{$(tot.cost)}</td>
              <td style={monoTd}>{tot.holDays}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p style={footnote}>
        Working days only — weekends excluded, and PTO falling on a public holiday isn&apos;t
        double counted. Cost is derived from each person&apos;s monthly rate and will not tie to
        Total Labor Cost, which uses the salaried fixed-cost model.
        {excludeOps && ' Non-billable roles are excluded by the toggle above.'}
      </p>
    </>
  );
}

function BarCard({ title, children }) {
  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
      <h4 style={{ fontSize:13, fontWeight:700, marginBottom:12, color:'var(--text2)' }}>{title}</h4>
      {children}
    </div>
  );
}

function BarRow({ label, value, width, color, valueColor }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, fontSize:12 }}>
      <div style={{ width:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:500 }}>{label}</div>
      <div style={{ flex:1, height:8, background:'var(--card2)', borderRadius:4, overflow:'hidden' }}>
        <div style={{ width:`${width}%`, height:'100%', borderRadius:4, background:color }} />
      </div>
      <div style={{ width:80, textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:11, color:valueColor||'var(--text2)' }}>{value}</div>
    </div>
  );
}

const thStyle     = { padding:'11px 14px', textAlign:'left', fontWeight:600, fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'.7px', color:'var(--text2)', whiteSpace:'nowrap', position:'sticky', top:0, background:'var(--card2)' };
const monoTd      = { padding:'9px 14px', fontFamily:'DM Mono,monospace', fontSize:12, whiteSpace:'nowrap' };
const carriedTag  = { fontSize:11, background:'var(--blue-bg)', color:'var(--blue)', borderRadius:3, padding:'1px 5px', fontWeight:700, marginLeft:6, cursor:'help', fontFamily:'DM Mono,monospace' };
const noticeStyle = { background:'var(--blue-bg)', border:'1px solid var(--info-border)', borderRadius:10, padding:'10px 16px', marginBottom:16, fontSize:13, color:'var(--info-text)', display:'flex', alignItems:'center', gap:8 };
const warnStyle   = { background:'var(--warn-bg)', border:'1px solid var(--warn-border)', borderRadius:10, padding:'10px 16px', marginBottom:16, fontSize:13, color:'var(--warn-text)', display:'flex', alignItems:'center', gap:8 };
const footnote    = { fontSize:11, color:'var(--text3)', marginTop:14, lineHeight:1.6 };
